import {t, Trans} from '@lingui/macro';
import _ from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {AsyncHandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {RadioButton} from '../../../libs/shared/ui/src/components/booleanInputs/radioButton';
import {DropdownBoxCoordinator} from '../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {NumberInputBox} from '../../../libs/shared/ui/src/components/boxedInputs/numberInputBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {SimpleDropdown} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {useNavigationBlocking} from '../../../srcCommon/app/navigationBlocking';
import {DateTimeFormatterBase} from '../../../srcCommon/formatters/dateTime/dateTimeFormatterBase';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {TimeComboBox} from '../../components/common/dateTime/timeComboBox';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {FolderBreadcrumb} from '../../components/folderBreadcrumbs/folderBreadcrumbs';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {RelativeTimeUnitsEnum, SettingsCustomSnoozeModel} from '../../models/api/customSnoozeModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum, SettingsSnoozesUrl} from '../../models/url/settingsUrlModel';
import {getTimeFormat} from '../../store/data/preferences/preferencesSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {SettingsBreadcrumbs} from '../common/settingsBreadcrumbs';
import {SettingsDiscardChangesDialog} from '../common/settingsDiscardChangesDialog';
import {
  SettingsFooter,
  StyledSettingsFormContent,
  StyledSettingsFormField,
  StyledSettingsFormTextBox,
} from '../common/settingsWrapper';
import {settingsHorizontalPaddingStyles} from '../settingsCommonConstants';
import {SettingsHeader} from '../settingsHeader';

/*
 * Props.
 */

interface SettingsSnoozeFormProps {
  snoozesUrl: SettingsSnoozesUrl;
  snooze?: SettingsCustomSnoozeModel;
  lastSnoozeId: number;
  isEditing?: boolean;
  onDeleteClick: AsyncHandlerOf<SettingsCustomSnoozeModel>;
  panelId: SettingsPanelId;
  onSaveClick: AsyncHandlerOf<SettingsCustomSnoozeModel>;
}

enum RelativeSnoozeUnitsEnum {
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}

/*
 * Constants.
 */

const SNOOZE_NAME_MAX_LENGTH = 20;

/*
 * Style.
 */

const StyledDiv = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  overflow: auto;

  position: relative;
`;

const StyledSettingsMain = styled.div`
  flex: 1;
  padding-bottom: 100px;
  ${settingsHorizontalPaddingStyles};
  display: flex;
  padding-right: 0;
  max-width: 650px;
`;

const StyledLabelDiv = styled.div`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade60};
`;
const StyledRowLabelDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.small};
`;
const StyledTimeComboBox = styled(TimeComboBox)`
  width: 160px;
`;
const StyledRadioButtonDiv = styled.div`
  padding-left: 10px;
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: auto;
  grid-gap: 4px;
`;
const StyledRadioButtonTitle = styled.div<{$isSelected: boolean}>`
  color: ${(p) => p.theme.greys.shade90};
  font-weight: ${(p) => (p.$isSelected ? fontWeights.bold : fontWeights.normal)};
  font-size: ${fontSizes.large};
`;

const StyledRadioContentDiv = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: auto;
  grid-gap: 10px;
  align-items: center;
  margin-left: 26px;
  margin-top: 11px;
`;

const StyledRadioDropdownsDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  column-gap: 10px;
  grid-auto-columns: auto;
`;

const StyledNumberInputBox = styled(NumberInputBox)`
  min-width: 6ch;
`;

const StyledRadioButtonsWrapper = styled.div`
  margin-top: -4px;
  border-top: 1px solid #e6e6e6;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

/*
 * Component.
 */

export const SettingsSnoozeForm: FC<SettingsSnoozeFormProps> = (props) => {
  const theme = useUserTheme();
  const {snoozesUrl, snooze, isEditing, panelId} = props;
  const [isSaving, setIsSaving] = useState(false);
  const [editedSnooze, setEditedSnooze] = useState<SettingsCustomSnoozeModel>(
    props.snooze || {
      id: props.lastSnoozeId + 1,
      name: '',
      offset: {},
      relativeTo: null,
    },
  );

  const {navigateToLink} = useLinkNavigation();
  const [shouldDisplayDeleteDialog, setShouldDisplayDeleteDialog] = useState(false);
  const canSave = useMemo(() => {
    if (isSaving) {
      return false;
    }

    const hasNonEmptyOffset =
      _(editedSnooze.offset)
        .values()
        .reject((v) => v === undefined)
        .value().length > 0;

    const baseCheck =
      !_.isEqual(props.snooze, editedSnooze) &&
      editedSnooze.name &&
      editedSnooze.name.length < SNOOZE_NAME_MAX_LENGTH;

    if (editedSnooze.relativeTo === null) {
      return Boolean(baseCheck);
    }

    return Boolean(baseCheck && hasNonEmptyOffset);
  }, [editedSnooze, isSaving, props.snooze]);

  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(panelId, canSave);
  const timeFormat = useAppSelector(getTimeFormat);
  const localize = useLocalize();

  const navigateToSnoozesOverview = () => {
    const newUrl = buildSettingsPath({
      ...snoozesUrl,
      resourcePage: SettingsResourcePagesEnum.OVERVIEW,
      snoozeId: undefined,
    });
    navigateToLink(newUrl);
  };

  const unblockAndNavigateToSnoozes = () => {
    unblockNavigation();
    navigateToSnoozesOverview();
  };

  const renderTopLine = () => {
    if (snoozesUrl.resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
      return null;
    }

    const breadcrumbs: ReadonlyArray<FolderBreadcrumb> = [];

    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        topLevelBreadcrumb={{
          label: <Trans id="settings-snoozes">Snoozes</Trans>,
          onBreadcrumbClick: navigateToSnoozesOverview,
        }}
        breadcrumbs={breadcrumbs}
      />
    );
  };
  const maybeRenderRightActions = () => {
    if (!isEditing) {
      return null;
    }

    return (
      <Button
        isRounded
        variant={ButtonVariantsEnum.SECONDARY_DANGER}
        onClick={() => setShouldDisplayDeleteDialog(true)}
      >
        <Trans id="settings-snoozes-edit-form-delete-action">Delete snooze</Trans>
      </Button>
    );
  };

  const maybeRenderDeleteDialog = () => {
    if (!isEditing || !shouldDisplayDeleteDialog) {
      return null;
    }

    const onDeleteSnooze = async () => {
      if (!props.snooze) {
        return;
      }

      setShouldDisplayDeleteDialog(false);
      await props.onDeleteClick(props.snooze);
      unblockAndNavigateToSnoozes();
    };

    return (
      <OkCancelDialog
        title={<Trans id="snoozesform-delete">Delete this snooze</Trans>}
        okButtonLabel={
          <Trans id="delete-snoozes-confirmation-dialog-delete-permanently">Delete permanently</Trans>
        }
        onRequestClose={() => setShouldDisplayDeleteDialog(false)}
        onOkClick={onDeleteSnooze}
      >
        <Trans id="snoozesform-delete-copy">
          This snooze will be permanently deleted. This action cannot be undone.
        </Trans>
      </OkCancelDialog>
    );
  };

  const maybeRenderDiscardChangesDialog = () => {
    if (!didBlockNavigation) {
      return null;
    }

    return <SettingsDiscardChangesDialog onCancel={cancelNavigation} onDiscard={unblockNavigation} />;
  };

  const maybeRenderFooter = () => (
    <SettingsFooter>
      <Button isRounded variant={ButtonVariantsEnum.SECONDARY} onClick={navigateToSnoozesOverview}>
        <Trans id="settings-form-cancel">Cancel</Trans>
      </Button>
      <Button
        isRounded
        variant={ButtonVariantsEnum.PRIMARY}
        onClick={async () => {
          setIsSaving(true);
          await props.onSaveClick(editedSnooze);
          setIsSaving(false);
        }}
        isDisabled={!canSave}
      >
        {isEditing ? (
          <Trans id="settings-form-save">Save</Trans>
        ) : (
          <Trans id="settings-form-create">Create</Trans>
        )}
      </Button>
    </SettingsFooter>
  );

  const renderAbsoluteDayValue = () => {
    if (editedSnooze.relativeTo !== null) {
      return null;
    }

    if (editedSnooze.offset.day === undefined) {
      return <Trans id="snoozesform-dayofsnooze">Day of snooze</Trans>;
    }

    const date = DateTime.local().set({weekday: editedSnooze.offset.day}).toJSDate();
    return <DateTimeFormatterBase format={{weekday: 'long'}} value={date} />;
  };

  const renderTimeComboBox = (setRelativeTo = false) => {
    const selectedTime =
      editedSnooze.offset.minute === undefined && editedSnooze.offset.hour === undefined
        ? undefined
        : DateTime.local().set({
            minute: editedSnooze.offset.minute ?? 0,
            hour: editedSnooze.offset.hour ?? 9,
          });

    return (
      <StyledRadioContentDiv>
        <StyledRowLabelDiv>
          <Trans id="snoozesform-at">At</Trans>
        </StyledRowLabelDiv>
        <StyledTimeComboBox
          noMargin
          showChevron
          dropdownWidth={170}
          timeFormat={timeFormat}
          selectedTime={selectedTime}
          emptyValue={localize(t('settings-snooze-timeofsnooze')`At time of snooze`)}
          onTimeSelect={(time) => {
            setEditedSnooze((prev) => {
              const relativeTo = setRelativeTo
                ? (time && RelativeTimeUnitsEnum.DAY) || RelativeTimeUnitsEnum.MINUTE
                : null;

              return {
                ...prev,
                relativeTo,
                offset: {
                  ...prev.offset,
                  minute: time?.minute,
                  hour: time?.hour,
                },
              };
            });
          }}
        />
      </StyledRadioContentDiv>
    );
  };

  const snoozeRelativeUnit = useMemo(() => {
    if (_.isInteger(editedSnooze.offset.month)) {
      return RelativeSnoozeUnitsEnum.MONTH;
    }
    if (_.isInteger(editedSnooze.offset.week)) {
      return RelativeSnoozeUnitsEnum.WEEK;
    }
    if (_.isInteger(editedSnooze.offset.day)) {
      return RelativeSnoozeUnitsEnum.DAY;
    }
    if (_.isInteger(editedSnooze.offset.hour)) {
      return RelativeSnoozeUnitsEnum.HOUR;
    }

    return RelativeSnoozeUnitsEnum.MINUTE;
  }, [
    editedSnooze.offset.day,
    editedSnooze.offset.hour,
    editedSnooze.offset.month,
    editedSnooze.offset.week,
  ]);

  const renderRelativeToRow = () => {
    const computeNumberInputValue = () => {
      if (_.isInteger(editedSnooze.offset.month)) {
        return editedSnooze.offset.month;
      }
      if (_.isInteger(editedSnooze.offset.week)) {
        return editedSnooze.offset.week;
      }
      if (_.isInteger(editedSnooze.offset.day)) {
        return editedSnooze.offset.day;
      }
      if (_.isInteger(editedSnooze.offset.hour)) {
        return editedSnooze.offset.hour;
      }

      return editedSnooze.offset.minute;
    };

    const onChange = (newValue: number | '' | undefined) => {
      if (!newValue) {
        return undefined;
      }

      if (_.isInteger(editedSnooze.offset.month)) {
        return setEditedSnooze((prev) => ({...prev, offset: {...prev.offset, month: newValue}}));
      }
      if (_.isInteger(editedSnooze.offset.week)) {
        return setEditedSnooze((prev) => ({...prev, offset: {...prev.offset, week: newValue}}));
      }
      if (_.isInteger(editedSnooze.offset.day)) {
        return setEditedSnooze((prev) => ({...prev, offset: {...prev.offset, day: newValue}}));
      }
      if (_.isInteger(editedSnooze.offset.hour)) {
        return setEditedSnooze((prev) => ({...prev, offset: {...prev.offset, hour: newValue}}));
      }

      return setEditedSnooze((prev) => ({...prev, offset: {...prev.offset, minute: newValue}}));
    };

    const renderDropdownValue = () => {
      if (_.isInteger(editedSnooze.offset.month)) {
        return <Trans id="snoozesform-months">Months</Trans>;
      }
      if (_.isInteger(editedSnooze.offset.week)) {
        return <Trans id="snoozesform-weeks">Weeks</Trans>;
      }
      if (_.isInteger(editedSnooze.offset.day)) {
        return <Trans id="snoozesform-days">Days</Trans>;
      }
      if (_.isInteger(editedSnooze.offset.hour)) {
        return <Trans id="snoozesform-hours">Hours</Trans>;
      }

      return <Trans id="snoozesform-minutes">Minutes</Trans>;
    };

    const numberInputValue = computeNumberInputValue() || 1;

    return (
      <StyledRadioContentDiv>
        <StyledRowLabelDiv>
          <Trans id="snoozesform-after">After</Trans>
        </StyledRowLabelDiv>
        <StyledRadioDropdownsDiv>
          <StyledNumberInputBox noMargin value={numberInputValue} onChange={onChange} min={1} />
          <DropdownBoxCoordinator
            shouldDisablePadding
            renderDropdown={(modalProps) => (
              <SimpleDropdown
                anchor={modalProps.anchor}
                onRequestClose={modalProps.onRequestClose}
                width={105}
              >
                <SimpleDropdownItem
                  isChecked={snoozeRelativeUnit === RelativeSnoozeUnitsEnum.MINUTE}
                  onSelect={() =>
                    setEditedSnooze((prev) => ({
                      ...prev,
                      relativeTo: RelativeTimeUnitsEnum.MINUTE,
                      offset: {
                        minute: numberInputValue,
                      },
                    }))
                  }
                >
                  <Trans id="snoozesform-minutes">Minutes</Trans>
                </SimpleDropdownItem>
                <SimpleDropdownItem
                  isChecked={snoozeRelativeUnit === RelativeSnoozeUnitsEnum.HOUR}
                  onSelect={() =>
                    setEditedSnooze((prev) => ({
                      ...prev,
                      relativeTo: RelativeTimeUnitsEnum.MINUTE,
                      offset: {
                        hour: numberInputValue,
                      },
                    }))
                  }
                >
                  <Trans id="snoozesform-hours">Hours</Trans>
                </SimpleDropdownItem>
                <SimpleDropdownItem
                  isChecked={snoozeRelativeUnit === RelativeSnoozeUnitsEnum.DAY}
                  onSelect={() =>
                    setEditedSnooze((prev) => ({
                      ...prev,
                      relativeTo: RelativeTimeUnitsEnum.MINUTE,
                      offset: {
                        day: numberInputValue,
                      },
                    }))
                  }
                >
                  <Trans id="snoozesform-days">Days</Trans>
                </SimpleDropdownItem>
                <SimpleDropdownItem
                  isChecked={snoozeRelativeUnit === RelativeSnoozeUnitsEnum.WEEK}
                  onSelect={() =>
                    setEditedSnooze((prev) => ({
                      ...prev,
                      relativeTo: RelativeTimeUnitsEnum.DAY,
                      offset: {
                        week: numberInputValue,
                      },
                    }))
                  }
                >
                  <Trans id="snoozesform-weeks">Weeks</Trans>
                </SimpleDropdownItem>
                <SimpleDropdownItem
                  isChecked={snoozeRelativeUnit === RelativeSnoozeUnitsEnum.MONTH}
                  onSelect={() =>
                    setEditedSnooze((prev) => ({
                      ...prev,
                      relativeTo: RelativeTimeUnitsEnum.DAY,
                      offset: {
                        month: numberInputValue,
                      },
                    }))
                  }
                >
                  <Trans id="snoozesform-months">Months</Trans>
                </SimpleDropdownItem>
              </SimpleDropdown>
            )}
            value={renderDropdownValue()}
          />
        </StyledRadioDropdownsDiv>
      </StyledRadioContentDiv>
    );
  };

  const maybeMessage = useMemo(
    () =>
      editedSnooze.name.length > SNOOZE_NAME_MAX_LENGTH ? (
        <Trans id="snoozesform-namemaxlength">Name cannot be longer than 20 characters</Trans>
      ) : null,
    [editedSnooze.name.length],
  );

  return (
    <StyledDiv>
      <SettingsHeader topLine={renderTopLine()} right={maybeRenderRightActions()}>
        {snooze && <Icon color={theme.greys.shade70} name="snoozeFilled" size={24} />}
        <div>{snooze ? snooze.name : <Trans id="settings-snooze-newsnooze">Add snooze option</Trans>}</div>
      </SettingsHeader>
      <StyledSettingsMain>
        <StyledSettingsFormContent>
          <StyledSettingsFormField>
            <StyledSettingsFormTextBox
              $width={258}
              label={<Trans id="snoozesform-name">Name</Trans>}
              value={editedSnooze.name}
              onChange={(newName) => {
                setEditedSnooze((prev) => ({...prev, name: newName}));
              }}
              placeholder={localize(t('settingssnooze-name-placeholder')`Add name`)}
              errorMessage={maybeMessage}
              hasError={Boolean(maybeMessage)}
              isRequired
              shouldAutoFocus
            />
          </StyledSettingsFormField>
          <StyledRadioButtonsWrapper>
            <RadioButton
              isChecked={editedSnooze.relativeTo === null}
              onChange={(bool) => {
                if (bool) {
                  setEditedSnooze((prev) => ({...prev, relativeTo: null}));
                } else {
                  setEditedSnooze((prev) => ({...prev, relativeTo: RelativeTimeUnitsEnum.DAY}));
                }
              }}
            >
              <StyledRadioButtonDiv>
                <StyledRadioButtonTitle $isSelected={editedSnooze.relativeTo === null}>
                  <Trans id="snoozesform-consistentday-title">Reopens on a day and time</Trans>
                </StyledRadioButtonTitle>
                <StyledLabelDiv>
                  <Trans id="snoozesform-consistentday-description">
                    Reopen conversations on a consistent day
                  </Trans>
                </StyledLabelDiv>
              </StyledRadioButtonDiv>
            </RadioButton>
            {editedSnooze.relativeTo === null && (
              <>
                <StyledRadioContentDiv>
                  <StyledRowLabelDiv>
                    <Trans id="snoozesform-day">Day</Trans>
                  </StyledRowLabelDiv>
                  <StyledRadioDropdownsDiv>
                    <DropdownBoxCoordinator
                      shouldDisablePadding
                      renderDropdown={(modalProps) => (
                        <SimpleDropdown
                          anchor={modalProps.anchor}
                          onRequestClose={modalProps.onRequestClose}
                          width={145}
                        >
                          <SimpleDropdownItem
                            key="day-of-snooze"
                            isChecked={editedSnooze.offset.day === undefined}
                            onSelect={() => {
                              setEditedSnooze((prev) => ({
                                ...prev,
                                offset: {...prev.offset, day: undefined},
                              }));
                            }}
                          >
                            <Trans id="snoozesform-dayofsnooze">Day of snooze</Trans>
                          </SimpleDropdownItem>
                          {Array.from({length: 7}).map((_v, index) => {
                            const date = DateTime.local().set({weekday: index}).toJSDate();
                            return (
                              <SimpleDropdownItem
                                key={`wd-${date.getDay()}`}
                                isChecked={editedSnooze.offset.day === index}
                                onSelect={() => {
                                  setEditedSnooze((prev) => ({
                                    ...prev,
                                    offset: {
                                      ...prev.offset,
                                      day: index,
                                    },
                                  }));
                                }}
                              >
                                <DateTimeFormatterBase format={{weekday: 'long'}} value={date} />
                              </SimpleDropdownItem>
                            );
                          })}
                        </SimpleDropdown>
                      )}
                      value={renderAbsoluteDayValue()}
                    />
                  </StyledRadioDropdownsDiv>
                </StyledRadioContentDiv>
                {renderTimeComboBox()}
              </>
            )}

            <RadioButton
              isChecked={editedSnooze.relativeTo !== null}
              onChange={(bool) => {
                if (bool) {
                  setEditedSnooze((prev) => ({
                    ...prev,
                    relativeTo: RelativeTimeUnitsEnum.MINUTE,
                    offset: {
                      minute: 1,
                    },
                  }));
                } else {
                  setEditedSnooze((prev) => ({...prev, relativeTo: null}));
                }
              }}
            >
              <StyledRadioButtonDiv>
                <StyledRadioButtonTitle $isSelected={editedSnooze.relativeTo !== null}>
                  <Trans id="snoozesform-amountoftime-title">Reopens after an amount of time</Trans>
                </StyledRadioButtonTitle>
                <StyledLabelDiv>
                  <Trans id="snoozesform-amountoftime-description">
                    Reopen conversations after a certain amount of time
                  </Trans>
                </StyledLabelDiv>
              </StyledRadioButtonDiv>
            </RadioButton>
            {editedSnooze.relativeTo !== null && (
              <>
                {renderRelativeToRow()}
                {[
                  RelativeSnoozeUnitsEnum.MONTH,
                  RelativeSnoozeUnitsEnum.WEEK,
                  RelativeSnoozeUnitsEnum.DAY,
                ].includes(snoozeRelativeUnit) && renderTimeComboBox(true)}
              </>
            )}
          </StyledRadioButtonsWrapper>
        </StyledSettingsFormContent>
        {maybeRenderDeleteDialog()}
        {maybeRenderDiscardChangesDialog()}
      </StyledSettingsMain>
      {maybeRenderFooter()}
    </StyledDiv>
  );
};
